import { DateTime } from "luxon";
import { NetFinderCommonModule } from "@/store/modules/net-finder/common";
import { VulnerabilityModule } from "@/store/modules/vuln-tracker/vulnerability";
import { VulnerabilityRHModule } from "@/store/modules/vuln-tracker/vulnerabilityRH";
import { DetectedManagedNodeModule } from "@/store/modules/vuln-tracker/detectedManagedNode";
import { AuthModule } from "@/store/modules/auth";
import { TeleworkManagerNode } from "@/store/modules/net-finder/util";
import vnfPb from "@/proto/vnf/vnf_pb";

const FORMAT_DATETIME = "yyyy-MM-dd HH:mm";
const FORMAT_DATE = "yyyy-MM-dd";
const FORMAT_YMDHMS = "yyyyMMddHHmmss";

export const getErrorString = (err: any) => {
  return typeof err === "string" ? err : "Fatal Error";
};

export const convertEpochToDatetime = (epoch: number) => {
  return epoch ? DateTime.fromSeconds(epoch).toFormat(FORMAT_DATETIME) : "";
};

export const convertEpochToYmdHms = (epoch: number) => {
  return epoch ? DateTime.fromSeconds(epoch).toFormat(FORMAT_YMDHMS) : "";
};

export const convertEpochToDate = (epoch: number) => {
  return epoch ? DateTime.fromSeconds(epoch).toFormat(FORMAT_DATE) : "";
};

export const convertIsoToEpoch = (timestamp: string) => {
  return DateTime.fromISO(timestamp).toSeconds();
};

export const convertIsoToDate = (timestamp: string) => {
  return DateTime.fromISO(timestamp).toFormat(FORMAT_DATE);
};

export const convertIsoToDatetime = (timestamp: string) => {
  return DateTime.fromISO(timestamp).toFormat(FORMAT_DATETIME);
};

export const maybeParser = <M>(callback: (item: any) => M, defaultValue: M) => {
  return (item: any): M => {
    try {
      return callback(item);
    } catch (_err) {
      return defaultValue;
    }
  };
};

export const maybeStrParser = (callback: (item: any) => string) => {
  return maybeParser(callback, "N/A");
};

export const maybeStrParserEmpty = (callback: (item: any) => string) => {
  return maybeParser(callback, "");
};

export const maybeStrParserDash = (callback: (item: any) => string) => {
  return maybeParser(callback, "--");
};

export const normalizeItem = (value: string) => {
  if (value == null) {
    return "";
  }
  if (value == "N/A") {
    return "";
  }
  value = normalizeAddr(value).toLowerCase();
  return value;
};

const normalizeAddr = (value: string) => {
  const regexSubnet = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\/\d{1,2}$/;
  const resultSubnet = value.match(regexSubnet);
  if (resultSubnet != null) {
    const splitted = value.split("/");
    let maskbits = splitted[1];
    if (maskbits.length == 1) {
      maskbits = "0" + maskbits;
    }
    return normalizeIP(splitted[0]) + "/" + maskbits;
  }
  const regex = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
  const result = value.match(regex);
  if (result != null) {
    return normalizeIP(value);
  }
  return value;
};

const normalizeIP = (value: string) => {
  const digits = value.split(".");
  const digit1 = "00" + digits[0];
  const digit2 = "00" + digits[1];
  const digit3 = "00" + digits[2];
  const digit4 = "00" + digits[3];
  return (
    digit1.substring(digit1.length - 3) +
    "." +
    digit2.substring(digit2.length - 3) +
    "." +
    digit3.substring(digit3.length - 3) +
    "." +
    digit4.substring(digit4.length - 3)
  );
};

export const initAllPageStates = () => {
  NetFinderCommonModule.teleworkManagerPageState.curPage = 1;
  NetFinderCommonModule.InitVtmHome();
  VulnerabilityModule.ResetPageState();
  VulnerabilityRHModule.ResetPageState();
  DetectedManagedNodeModule.ResetPageState();
  NetFinderCommonModule.InitEmployeeInfoList();
};

export const convertFromWeekDay = (weekDay: string) => {
  switch (weekDay) {
    case "sunday":
      return "日曜日";
    case "monday":
      return "月曜日";
    case "tuesday":
      return "火曜日";
    case "wednesday":
      return "水曜日";
    case "thursday":
      return "木曜日";
    case "friday":
      return "金曜日";
    case "saturday":
      return "土曜日";
    default:
      return "";
  }
};

export const convertStatus = (status: string) => {
  switch (status) {
    case "requested":
      return { label: "進行中", icon: "autorenew", color: "light-blue" };
    case "rejected":
      return { label: "失敗", icon: "close", color: "red" };
    case "started":
      return { label: "進行中", icon: "autorenew", color: "light-blue" };
    case "succeeded":
      return { label: "完了", icon: "check", color: "green" };
    case "failed":
      return { label: "失敗", icon: "close", color: "red" };
    case "aborted":
      return { label: "中断", icon: "close", color: "red" };
    default:
      return { label: "", icon: null, color: "primary" };
  }
};

export const convertDiscoveryStatus = (status: string) => {
  switch (status) {
    case "new":
      return { label: "新規", color: "orange" };
    case "stable":
      return { label: "既存", color: "light-blue" };
    case "outdated":
      return { label: "消失", color: "red" };
    default:
      return { label: "不明", color: "grey-darken2" };
  }
};

export const convertOnlineStatus = (status: string) => {
  switch (status) {
    case "online":
      return {
        label: "オンライン",
        icon: "check_circle",
        color: "green",
        order: "1",
      };
    case "offline":
      return {
        label: "オフライン",
        icon: "remove_circle",
        color: "grey",
        order: "2",
      };
    default:
      return { label: "不明", icon: "cancel", color: "red", order: "3" };
  }
};

export const convertScanTaskStatus = (status: string) => {
  switch (status) {
    case "requested":
      return { label: "進行中", icon: "autorenew" };
    case "rejected":
      return { label: "失敗", icon: "close" };
    case "started":
      return { label: "進行中", icon: "autorenew" };
    case "succeeded":
      return { label: "完了", icon: "check" };
    case "failed":
      return { label: "失敗", icon: "close" };
    case "aborted":
      return { label: "中断", icon: "close" };
    default:
      return { label: "", icon: null };
  }
};

export const convertApproved = (approved: boolean) => {
  return approved ? "承認済" : "未承認";
};

export const convertAggregationType = (type: string) => {
  switch (type) {
    case "node":
      return { value: "端末", order: "1" };
    default:
      return { value: "IP", order: "2" };
  }
};

export const convertLocation = (location: string) => {
  switch (location) {
    case "remote":
      return { label: "リモート", icon: "home", order: "1" };
    case "office":
      return { label: "社内", icon: "apartment", order: "2" };
    default:
      return { label: "不明", icon: "not_listed_location", order: "3" };
  }
};

export const convertVpnStatus = (node: TeleworkManagerNode) => {
  if (node.userInfo.vpnAccount === "") {
    return {
      value: "none",
      label: "なし",
      icon: "remove_circle",
      color: "grey",
      order: "4",
    };
  }
  if (node.session) {
    if (node.session.logon) {
      return {
        value: "online",
        label: "接続中",
        icon: "check_circle",
        color: "green",
        order: "1",
      };
    } else {
      return {
        value: "offline",
        label: "未接続",
        icon: "cancel",
        color: "grey",
        order: "2",
      };
    }
  }
  return {
    value: "unknown",
    label: "不明",
    icon: "remove_circle",
    color: "grey",
    order: "3",
  };
};

export const convertSecurityStatus = (status: string) => {
  switch (status) {
    case "secure":
      return { icon: "lock", color: "green", order: "1" };
    case "unsecure":
      return { icon: "lock_open", color: "grey darken-1", order: "2" };
    default:
      return { icon: "remove", color: "grey darken-1", order: "" };
  }
};

export const convertWindowsUpdateStatus = (status: string) => {
  switch (status) {
    case "latest":
      return { label: "未適用なし", icon: "", color: "", order: "2" };
    case "not_latest":
      return { label: "未適用あり", icon: "error", color: "red", order: "1" };
    case "no_terminal":
      return { label: "端末未登録", icon: "", color: "", order: "" };
    case "not_windows":
      return { label: "非Windows", icon: "", color: "", order: "" };
    default:
      return { label: "", icon: "", color: "", order: "" };
  }
};

export const convertNodeMarker = (index: number) => {
  switch (index) {
    case 0:
      return { icon: "check_box", style: "round" };
    default:
      return { icon: "help_center", style: "outline" };
  }
};

export const convertImportAction = (action: string) => {
  switch (action) {
    case "insert":
      return { label: "新規", icon: "add" };
    case "update":
      return { label: "変更", icon: "edit" };
    case "delete":
      return { label: "削除", icon: "delete_outline" };
    default:
      return { label: "", icon: "" };
  }
};

export const baseDate = () => {
  const today = new Date();
  // 基準日の切替時に合わせて調整
  today.setHours(today.getHours() - 12);
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const d = today.getDate();
  return y + ("0" + m).slice(-2) + ("0" + d).slice(-2);
};

// eslint-disable-next-line
export const datePickerDayFormat = (date: string, locale: string) => {
  return Number(date.slice(-2));
};

export const isWindows = (node: any) => {
  return (
    node != null &&
    node.extraField != null &&
    node.extraField.windows != null &&
    node.extraField.windows.version != null
  );
};

export const getPermission = (target: string) => {
  const user = AuthModule.user;
  const board = AuthModule.currentBoard;
  if (user != null && board != null) {
    const uid = user.id;
    const permission = board.data.permissions.find((item) => item.uid === uid);
    if (permission != null) {
      const perm = Object.entries(permission).find(
        (item) => item[0] === target,
      );
      if (perm != null) {
        return perm[1];
      }
    }
  }
  return "none";
};

export const convertUserName = (
  userInfo: vnfPb.UserInfo.AsObject | undefined,
): string => {
  if (!userInfo) return "";
  if (userInfo.employee) {
    return userInfo.employee.name ? userInfo.employee.name : "";
  } else {
    return userInfo.name ? userInfo.name : "";
  }
};

export const convertUserDepartment = (
  userInfo: vnfPb.UserInfo.AsObject | undefined,
): string => {
  if (!userInfo) return "";
  if (userInfo.employee) {
    return userInfo.employee.department ? userInfo.employee.department : "";
  } else {
    return userInfo.department ? userInfo.department : "";
  }
};

const Encoding = require("encoding-japanese");
export const downloadText = (
  text: string,
  filename: string,
  enc: string,
  type: string,
) => {
  const bytes = Encoding.convert(text, {
    from: "UNICODE",
    to: enc,
    type: "array",
  });
  const blob = new Blob([Uint8Array.from(bytes)], { type: type });
  const link = document.createElement("a");
  link.href = window.URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
};

export function isIpAddressOrCidr(value: string) {
  const regex =
    /^((25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])\.){3}(25[0-5]|2[0-4][0-9]|1[0-9][0-9]|[1-9]?[0-9])(\/(3[0-1]|[1-2]?[0-9]))?$/;
  return regex.test(value);
}
